import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getDiscount, updateDiscount } from "../../api/Discounts"; 
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import DiscountForm from "../../components/discount/DiscountForm";

const CreateDiscountPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth(); 
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  const [discount, setDiscount] = useState({
    name: "", 
    description: "", 
    discountValueType: [], 
    discountConditionType: "Volume",
    isCalculatedPerChargingSite: false, 
    minKwh: 0,
    maxKwh: 0,
    discountValue: 0,
    startDateTimeUtc: "",
    endDateTimeUtc: "",
  });
  const [originalDiscount, setOriginalDiscount] = useState({});

const numericKeys = ["minKwh", "maxKwh", "discountValue"];

const handleInputChange = (key, value) => {
  setDiscount((prev) => ({
    ...prev,
    [key]: numericKeys.includes(key) 
      ? value === "" ? "" : Number(value)
      : value
  }));
};

  useEffect(() => { 
    handleGetChargingDiscount();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  useEffect(() => {
  setHasChanges(JSON.stringify(discount) !== JSON.stringify(originalDiscount));
  }, [discount, originalDiscount]);

  const formatForInput = (iso) => {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 16); 
};

  const normalize = (d) => ({
  name: d.name ?? "",
  description: d.description ?? "",
  discountValueType: d.discountValueType ?? "Fixed",
  discountConditionType: d.discountConditionType ?? "Volume",
  isCalculatedPerChargingSite: !!d.isCalculatedPerChargingSite,
  minKwh: Number(d.minKwh ?? 0),
  maxKwh: Number(d.maxKwh ?? 0),
  discountValue: Number(d.discountValue ?? 0),
  startDateTimeUtc: formatForInput(d.startDateTimeUtc),
  endDateTimeUtc: formatForInput(d.endDateTimeUtc),
  discountPublicId: d.discountPublicId, 
});

  // Form submission handler
  const handleGetChargingDiscount = async () => {
  setLoading(true);  
    try {
      const accessToken = await getAccessToken();
      const newDiscount = await getDiscount(id, accessToken);
      const normalizedDiscount = normalize(newDiscount);

      setDiscount(normalizedDiscount);
      setOriginalDiscount(normalizedDiscount);
      setLoading(false);
    } catch (error) {
      setLoading(true);  
    } 
  };

  const handleUpdateDiscount = async () => {
    const toastId = toast.loading(t("toast.updating", { entity: t("entity.discount") }));  

    try {
      const accessToken = await getAccessToken();
      const payload = {
      ...discount,
      startDateTimeUtc: discount.startDateTimeUtc ? new Date(discount.startDateTimeUtc).toISOString() : null,
      endDateTimeUtc: discount.endDateTimeUtc ? new Date(discount.endDateTimeUtc).toISOString() : null,
    };

    const newDiscount = await updateDiscount(payload, accessToken);

      toast.success(t("toast.updated", { entity: t("entity.discount") }), { id: toastId });

      navigate(`/discount/${newDiscount.discountPublicId}`);
      
    } catch (error) {
        toast.error(error?.message || t("toast.failedUpdate", { entity: t("entity.discount") }), {
        id: toastId,
      });
    } 
  };

  const navigateBack = () => {
      if (navigate.length > 1) {
        navigate(-1);
      } else {
        navigate("/discounts");
      }
  }

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">

          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={t("pages.editDiscount")} loading={loading} />
          </div>

          <div className={`${loading ? "loading" : ""} page-content`}>
            <div className="flex flex-row items-center pb-4">
              <button
                onClick={() => navigateBack()}
                className="text-black flex"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">{discount?.name || t("common.unknown")}</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigateBack()}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={() =>{handleUpdateDiscount()}}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            <DiscountForm discount={discount} handleInputChange={handleInputChange} />
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateDiscountPage;
